import React, { useEffect } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import Box from '@src/components/Box'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { PricingAvailabilityItem } from '@src/aem-content/aem-types'
import { useIntl } from 'react-intl'
import DiscoPromoMaterial from './DiscoPromoMaterial'

export interface AddToCartFormValues {
  [key: string]: {
    materialNumber: string
    quantity: number | string
    dealerId: string | null
  }
}

type DiscoPromoProductPricingProps = {
  product?: string
  materialPricing: PricingAvailabilityItem[]
  dchainMessage?: string
  hideAddToCartForPrepackItems: boolean
  submitForm: () => void
  isSubmitting: boolean
  dirty: boolean
  resetForm: (nextState?: { values?: AddToCartFormValues }) => void
  setFieldValue: (field: string, value: any) => void
  values: AddToCartFormValues
  isMarketplace?: boolean
  canAddToCart: boolean
  isMarketplaceCartEnabled: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
  MaterialContainer: {
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2, 0, 1 / 2, 0),
      '& th': {
        padding: theme.spacing(2),
        borderBottom: 'none',
      },
      '& td': {
        padding: theme.spacing(2),
        borderBottom: 'none',
      },
    },
  },
  materialHeadRow: {
    fontSize: theme.typography.pxToRem(12),
  },
  materialRow: {
    fontSize: theme.typography.pxToRem(14),
  },
  emptyCell: {
    [theme.breakpoints.up('md')]: {
      width: '13%',
      minWidth: 120,
    },
  },
  sku: {
    [theme.breakpoints.up('md')]: {
      width: '20%',
      minWidth: 180,
    },
  },
  packsize: {
    [theme.breakpoints.up('md')]: {
      width: '22%',
    },
  },
  price: {
    [theme.breakpoints.up('md')]: {
      width: '15%',
      textAlign: 'center',
    },
  },
}))

const DiscoPromoProductPricing = ({
  product,
  materialPricing,
  dchainMessage,
  submitForm,
  setFieldValue,
  values,
  isSubmitting,
  dirty,
  canAddToCart,
  hideAddToCartForPrepackItems,
  isMarketplace,
  isMarketplaceCartEnabled,
}: DiscoPromoProductPricingProps) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()

  useEffect(() => {
    if (
      dchainMessage === null &&
      materialPricing.every((item) => item?.availableQtyInStock === null)
    ) {
      Object.keys(values).forEach((key) => {
        setFieldValue(key, {
          ...values[key],
          quantity: '1',
        })
      })
    }
  }, [])

  return (
    <Box key={product}>
      <TableContainer className={classes.MaterialContainer}>
        <Table>
          <TableHead className={classes.materialHeadRow}>
            <TableRow>
              <TableCell className={classes.emptyCell}></TableCell>
              <TableCell className={classes.sku}>
                {formatMessage({ id: 'SKU', defaultMessage: 'SKU' })}
              </TableCell>
              <TableCell className={classes.packsize}>
                {' '}
                {formatMessage({
                  id: 'PACK_SIZE',
                  defaultMessage: 'Pack Size',
                })}
              </TableCell>
              <TableCell className={classes.price}>
                {formatMessage({ id: 'PRICE', defaultMessage: 'Price' })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {materialPricing?.map((material, index) => (
              <TableRow key={material.product} className={classes.materialRow}>
                <DiscoPromoMaterial
                  index={index}
                  material={material}
                  submitForm={submitForm}
                  isSubmitting={isSubmitting}
                  dirty={dirty}
                  canAddToCart={canAddToCart}
                  hideAddToCartForPrepackItems={hideAddToCartForPrepackItems}
                  isMarketplace={isMarketplace}
                  isMarketplaceCartEnabled={isMarketplaceCartEnabled}
                />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default DiscoPromoProductPricing
